import * as XLSX from 'xlsx';
import { LTSSService, Program, ServiceStatus, ClaimHistory } from '../types/ltss';

export async function parseExcelFile(file: File): Promise<LTSSService[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const isCSV = file.name.toLowerCase().endsWith('.csv');
    
    reader.onload = (e) => {
      try {
        let workbook: XLSX.WorkBook;
        
        if (isCSV) {
          // For CSV files, read as text and parse
          const csvText = e.target?.result as string;
          workbook = XLSX.read(csvText, { type: 'string', cellDates: true });
        } else {
          // For Excel files, read as array buffer
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          workbook = XLSX.read(data, { type: 'array', cellDates: true });
        }
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null, raw: false });
        
        console.log('Parsed rows:', jsonData.length);
        console.log('First row keys:', jsonData.length > 0 ? Object.keys(jsonData[0] as object) : 'No rows');
        console.log('First row sample:', jsonData[0]);
        
        // Debug: Check for date-related columns
        if (jsonData.length > 0) {
          const firstRow = jsonData[0] as Record<string, any>;
          const dateColumns = Object.keys(firstRow).filter(k => 
            k.toLowerCase().includes('date') || k.toLowerCase().includes('day')
          );
          console.log('Date-related columns found:', dateColumns);
          dateColumns.forEach(col => {
            console.log(`  ${col}:`, firstRow[col], '(type:', typeof firstRow[col], ')');
          });
        }
        
        // LTSS reports have grouped rows where client info only appears on first row
        // We need to forward-fill the client info
        const services = forwardFillAndMap(jsonData);
        console.log('Mapped services:', services.length);
        resolve(services);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    
    // Read CSV as text, Excel as ArrayBuffer
    if (isCSV) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
}

// Helper to safely parse dates from various formats
function parseDate(value: any): Date | null {
  if (!value) return null;
  
  // If it's already a Date object (from cellDates: true)
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  
  // If it's a number (Excel serial date)
  if (typeof value === 'number') {
    // Excel serial date: days since 1900-01-01 (with a bug for 1900 leap year)
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // If it's a string, try to parse it
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    
    // Try standard Date parsing
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) return parsed;
    
    // Try MM/DD/YYYY format
    const parts = trimmed.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts.map(p => parseInt(p, 10));
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) return date;
    }
  }
  
  return null;
}

function forwardFillAndMap(rows: any[]): LTSSService[] {
  const services: LTSSService[] = [];
  let currentClient: Partial<LTSSService> = {};
  
  for (const row of rows) {
    // Forward-fill client-level fields
    if (row['Client Name']) {
      currentClient = {
        clientName: row['Client Name'],
        clientId: row['Client ID'],
        clientMA: (row['Client MA#'] || row['Client MA'])?.toString() || '',
        providerName: row['Provider Name'],
        providerNumber: row['Provider Number']?.toString() || '',
      };
    }
    
    // Forward-fill week-level fields
    if (row['Week']) {
      currentClient.week = row['Week'];
      currentClient.weeklyPosHours = parseFloat(row['Weekly POS Hours'] || row['Authorized Hours per Week']) || 0;
    }

    // Forward-fill service type and program
    if (row['Service Type']) {
      currentClient.serviceType = row['Service Type'];
    }
    if (row['Program']) {
      currentClient.program = row['Program'] as Program;
    }
    
    // Map the row to LTSSService
    const service: LTSSService = {
      clientName: currentClient.clientName || '',
      clientId: currentClient.clientId || '',
      clientMA: currentClient.clientMA || '',
      providerName: currentClient.providerName || '',
      providerNumber: currentClient.providerNumber || '',
      week: currentClient.week || '',
      weeklyPosHours: currentClient.weeklyPosHours || 0,
      serviceType: currentClient.serviceType || '',
      temporaryService: row['Temporary Service'] === 'Yes',
      emergencyService: row['Emergency Service'] === 'Yes',
      program: currentClient.program || 'CO',
      posHours: parseFloat(row['POS Hours']) || currentClient.weeklyPosHours || 0,
      dayOfService: row['Day Of Service'] || row['Day of Service'] || row['DayOfService'] || '',
      serviceDate: parseDate(row['Service Date'] || row['Date Of Service'] || row['Date of Service'] || row['ServiceDate']),
      startTime: row['Start Time'] || null,
      endTime: row['End Time'] || null,
      serviceLength: parseFloat(row['Service Length (Hours)']) || null,
      totalServiceLength: parseFloat(row['Total Service Length (Hours)']) || null,
      staffName: row['Staff Name'] || '',
      staffId: row['Staff ID'] || '',
      staffLoginId: row['Staff Login ID']?.toString() || '',
      serviceInitiationSource: row['Service Initiation Source'] || '',
      procedureCode: row['Procedure Code'] || '',
      serviceStatus: (row['Service Status '] || row['Service Status']) as ServiceStatus,
      totalServiceLengthUnits: parseFloat(row['Total Service Length (Units)']) || null,
      unitsBilled: parseFloat(row['Units Billed']) || null,
      claimHistory: row['Claim History'] as ClaimHistory || null,
      amountPaid: parseFloat(row['Amount Paid in ISAS (Gross)*'] || row['Amount Paid in PP (Gross)*']) || null,
      pendingAdjustmentStartTime: row['Pending Adjustment Start Time'] || null,
      pendingAdjustmentEndTime: row['Pending Adjustment End Time'] || null,
      pendingAdjustmentDuration: parseFloat(row['Pending Adjustment Duration']) || null,
      pendingAdjustmentUnits: parseFloat(row['Pending Adjustment Units']) || null,
      pendingAdjustmentStatus: row['Pending Adjustment Status'] || null,
      serviceComments: row['Service Comments'] || null,
      claimComments: row['Claim Comments'] || null,
      evvAppClockInLocation: row['Evv App Clock In Location'] || null,
      gpsClockInLocation: row['Gps Clock In Location'] || null,
      evvAppClockOutLocation: row['Evv App Clock Out Location'] || null,
      gpsClockOutLocation: row['Gps Clock Out Location'] || null,
    };
    
    // Only add if we have valid service data
    if (service.staffName || service.startTime || service.endTime || service.dayOfService) {
      services.push(service);
    }
  }
  
  return services;
}
